import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { ImageUpload } from "./ImageUpload";

const EMPTY = { name: "", location: "", role: "", earning: "", quote: "", image: "", visible: true, location_hi: "", role_hi: "", quote_hi: "" };
const FIELDS = [["name", "Name"], ["earning", "Earning badge"], ["location", "Location (English)"], ["location_hi", "Location (Hindi)"], ["role", "Role (English)"], ["role_hi", "Role (Hindi)"]];

export const TestimonialsPanel = () => {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);

  useEffect(() => {
    api.get("/admin/testimonials").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errMsg(e)));
  }, []);

  const save = async (item) => {
    try {
      const body = { ...EMPTY, ...item };
      delete body.id; delete body.created_at;
      const { data } = item.id ? await api.put(`/admin/testimonials/${item.id}`, body) : await api.post("/admin/testimonials", body);
      setList((p) => (item.id ? p.map((x) => (x.id === data.id ? data : x)) : [...p, data]));
      setEdit(null);
      toast.success("Saved");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    await api.delete(`/admin/testimonials/${id}`).catch(() => {});
    setList((p) => p.filter((x) => x.id !== id));
  };

  return (
    <div data-testid="admin-testimonials">
      <button className="btn-primary" onClick={() => setEdit({ ...EMPTY })} data-testid="testimonial-add-btn"><Plus size={16} /> Add testimonial</button>
      <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {list.map((t) => (
          <div key={t.id} className="bento flex flex-col" data-testid={`admin-testimonial-${t.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                {t.image && <img src={t.image} alt={t.name} className="h-12 w-12 rounded-xl object-cover" />}
                <div><p className="font-semibold">{t.name}</p><p className="text-xs text-slate-400">{t.role} · {t.location}</p>{t.quote_hi && <span className="mt-1 inline-block rounded bg-saffron/15 px-1.5 py-0.5 text-[10px] text-orange-300">EN + हिं</span>}</div>
              </div>
              <Switch checked={t.visible} onCheckedChange={(v) => save({ ...t, visible: v })} data-testid={`testimonial-visible-${t.id}`} />
            </div>
            <p className="mt-4 flex-1 text-sm text-slate-300">“{t.quote}”</p>
            <div className="mt-4 flex gap-2">
              <button className="btn-ghost" onClick={() => setEdit(t)} data-testid={`testimonial-edit-${t.id}`}><Pencil size={14} /> Edit</button>
              <button className="btn-ghost text-red-300" onClick={() => remove(t.id)} data-testid={`testimonial-delete-${t.id}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {!list.length && <p className="text-sm text-slate-400" data-testid="testimonials-empty">No testimonials yet. The section stays hidden on the website until you add one.</p>}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-card text-white">
          <DialogHeader><DialogTitle>{edit?.id ? "Edit" : "New"} testimonial</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <ImageUpload value={edit.image} onChange={(url) => setEdit({ ...edit, image: url })} testid="testimonial-photo" label="Partner photo" />
              {FIELDS.map(([k, l]) => (
                <label key={k} className="block"><span className="text-xs uppercase tracking-wider text-slate-400">{l}</span>
                  <input className="mt-1 h-10 w-full rounded-lg border border-white/10 bg-ink px-3 text-sm outline-none focus:border-saffron" value={edit[k] ?? ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} data-testid={`testimonial-input-${k}`} />
                </label>
              ))}
              <label className="block"><span className="text-xs uppercase tracking-wider text-slate-400">Quote (English)</span>
                <textarea rows={4} className="mt-1 w-full rounded-lg border border-white/10 bg-ink p-3 text-sm outline-none focus:border-saffron" value={edit.quote} onChange={(e) => setEdit({ ...edit, quote: e.target.value })} data-testid="testimonial-input-quote" />
              </label>
              <label className="block"><span className="text-xs uppercase tracking-wider text-slate-400">Quote (Hindi — shown when visitor picks हिं)</span>
                <textarea rows={4} className="mt-1 w-full rounded-lg border border-white/10 bg-ink p-3 text-sm outline-none focus:border-saffron" value={edit.quote_hi ?? ""} onChange={(e) => setEdit({ ...edit, quote_hi: e.target.value })} data-testid="testimonial-input-quote_hi" />
              </label>
              <p className="text-xs text-slate-400">Hindi fields are optional — if empty, the English text is shown in both languages.</p>
              <button className="btn-primary w-full justify-center" onClick={() => save(edit)} data-testid="testimonial-save-btn">Save</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
